/**
 * Utilitaire API centralisé avec gestion automatique des erreurs d'authentification
 */

// Event personnalisé pour signaler une erreur d'auth
export const AUTH_ERROR_EVENT = 'auth:error';

/**
 * Décoder un JWT sans vérification de signature (côté client)
 */
export const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

/**
 * Vérifier si un token JWT est expiré
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  // Ajouter une marge de 60 secondes pour éviter les problèmes de timing
  const now = Date.now() / 1000;
  return decoded.exp < now + 60;
};

/**
 * Obtenir le temps restant avant expiration (en secondes)
 */
export const getTokenTimeRemaining = (token) => {
  if (!token) return 0;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return 0;
  
  const now = Date.now() / 1000;
  return Math.max(0, decoded.exp - now);
};

/**
 * Dispatcher un événement d'erreur d'authentification
 */
const dispatchAuthError = (reason = 'expired') => {
  window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT, { detail: { reason } }));
};

/**
 * Fetch authentifié avec gestion automatique des erreurs 401
 * @param {string} url - URL de l'API
 * @param {object} options - Options fetch (method, body, etc.)
 * @returns {Promise<Response>}
 */
export const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem('portalToken');
  
  // Vérifier si le token est expiré avant d'envoyer la requête
  if (isTokenExpired(token)) {
    dispatchAuthError('expired');
    throw new Error('Session expirée');
  }
  
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`
  };
  
  // Ajouter Content-Type si body est présent et pas de FormData
  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }
  
  const response = await fetch(url, { ...options, headers });
  
  // Gérer les erreurs 401
  if (response.status === 401) {
    dispatchAuthError('unauthorized');
    throw new Error('Non autorisé');
  }
  
  return response;
};

/**
 * Fetch authentifié qui retourne directement le JSON
 */
export const authFetchJson = async (url, options = {}) => {
  const response = await authFetch(url, options);
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erreur serveur' }));
    throw new Error(error.error || error.message || 'Erreur serveur');
  }
  
  return response.json();
};

/**
 * Hook pour les requêtes GET simples
 */
export const fetchWithAuth = async (url) => {
  return authFetchJson(url, { method: 'GET' });
};

/**
 * Hook pour les requêtes POST
 */
export const postWithAuth = async (url, data) => {
  return authFetchJson(url, {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

/**
 * Hook pour les requêtes PUT
 */
export const putWithAuth = async (url, data) => {
  return authFetchJson(url, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

/**
 * Hook pour les requêtes PATCH
 */
export const patchWithAuth = async (url, data) => {
  return authFetchJson(url, {
    method: 'PATCH',
    body: JSON.stringify(data)
  });
};

/**
 * Hook pour les requêtes DELETE
 */
export const deleteWithAuth = async (url) => {
  return authFetchJson(url, { method: 'DELETE' });
};

export default {
  authFetch,
  authFetchJson,
  fetchWithAuth,
  postWithAuth,
  putWithAuth,
  patchWithAuth,
  deleteWithAuth,
  isTokenExpired,
  getTokenTimeRemaining,
  decodeToken,
  AUTH_ERROR_EVENT
};
